import { ManualRedispatchResult } from '@/console/api/types'

/**
 * F-OPT 재발사 패널의 순수 로직. 계산은 전부 여기 두고 `RedispatchPanel` 은 이 모듈의 결과만
 * 그린다(Task 6 이 세운 브리프 원칙 — 화면 컴포넌트에 계산을 넣지 않는다).
 *
 * 핸드오프 §4.2 함정: 입력은 주문번호가 아니라 `standard_order.id`(Long) 다.
 *
 * LOGISPRD-13188 — 서버는 한 요청에 100건까지만 받는다(`MANUAL_REDISPATCH_MAX_TARGETS`). 5분 동기
 * 호출이라 서버 상한을 올리지 않고, 화면이 1000건까지 받아 `REDISPATCH_BATCH_SIZE` 씩 잘라 순서대로 보낸다.
 */
export const MAX_REDISPATCH_IDS = 1000
export const REDISPATCH_BATCH_SIZE = 100

export const chunkIds = (ids: number[], size = REDISPATCH_BATCH_SIZE): number[][] =>
  Array.from({ length: Math.ceil(ids.length / size) }, (_, i) => ids.slice(i * size, (i + 1) * size))

/** 확인 다이얼로그의 대기 안내. 한 묶음이면 기존 문구 그대로 둔다. */
export const describeRedispatchWait = (count: number): string => {
  const batches = Math.ceil(count / REDISPATCH_BATCH_SIZE)
  return batches <= 1
    ? '응답까지 최대 5분'
    : `${REDISPATCH_BATCH_SIZE}건씩 ${batches}번 나눠 순서대로 보내고, 한 번에 최대 5분(전체 최대 ${batches * 5}분)`
}

export type ParsedOrderIds = { ids: number[]; invalid: string[]; duplicates: number; overLimit: boolean }

/** 쉼표·줄바꿈을 구분자로 보고, distinct(입력 순서 유지) 후 숫자가 아닌 값은 `invalid` 로 따로 모은다. */
export const parseStandardOrderIds = (raw: string): ParsedOrderIds => {
  const pieces = raw
    .split(/[,\n]/)
    .map((piece) => piece.trim())
    .filter((piece) => piece.length > 0)

  const seen = new Set<number>()
  const ids: number[] = []
  const invalid: string[] = []
  let duplicates = 0

  pieces.forEach((piece) => {
    // PR 리뷰 Warning — 음수와 안전 정수 범위를 벗어난 값을 거른다. `standard_order.id` 는 DB
    // auto-increment PK(Long) 라 음수가 될 수 없고, `Number.MAX_SAFE_INTEGER` 를 넘으면 JS 가
    // 값을 정확히 표현하지 못해 엉뚱한 주문을 재발사할 수 있다. 서버도 400 으로 막지만, 실제
    // 주문을 다시 쏘는 조치라 요청을 보내기 전에 화면에서 끊는다.
    if (!/^\d+$/.test(piece)) {
      invalid.push(piece)
      return
    }

    const id = Number(piece)
    if (!Number.isSafeInteger(id)) {
      invalid.push(piece)
      return
    }
    if (seen.has(id)) {
      duplicates += 1
      return
    }

    seen.add(id)
    ids.push(id)
  })

  return { ids, invalid, duplicates, overLimit: ids.length > MAX_REDISPATCH_IDS }
}

export type RedispatchOutcome = 'submitted' | 'dedup' | 'accepted-not-submitted' | 'failed'

/**
 * 결과 4분류. 우선순위: `!accepted` → failed · `dedup` → dedup · `routedToOptimizer` → submitted ·
 * 나머지 → accepted-not-submitted. `accepted` 단독으로 성공을 판정하지 않는다(핸드오프 §4.2 함정).
 */
export const classifyRedispatchResult = (r: ManualRedispatchResult): RedispatchOutcome => {
  if (!r.accepted) {
    return 'failed'
  }

  if (r.dedup) {
    return 'dedup'
  }

  if (r.routedToOptimizer) {
    return 'submitted'
  }

  return 'accepted-not-submitted'
}

export const redispatchOutcomeLabel = (o: RedispatchOutcome): { label: string; tone: 'good' | 'neutral' | 'warn' | 'crit' } => {
  switch (o) {
    case 'submitted':
      return { label: '제출됨', tone: 'good' }
    case 'dedup':
      return { label: '중복 흡수', tone: 'neutral' }
    case 'accepted-not-submitted':
      return { label: '접수 · 미제출', tone: 'warn' }
    case 'failed':
      return { label: '실패', tone: 'crit' }
    default:
      return { label: '실패', tone: 'crit' }
  }
}

/** 결과 행의 원인 문구 — `RedispatchPanel` 의 `RowList` `.ds` 칸. 실패는 `failureReason` 원문을 그대로 낸다. */
export const describeRedispatchResult = (r: ManualRedispatchResult): string => {
  const outcome = classifyRedispatchResult(r)

  if (outcome === 'failed') {
    return r.failureReason ?? '사유 없음'
  }

  if (outcome === 'dedup') {
    return 'dedup = true'
  }

  if (outcome === 'submitted') {
    return 'routedToOptimizer = true'
  }

  return `accepted = true · routedToOptimizer = false${r.failureReason ? ` · ${r.failureReason}` : ''}`
}
